import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  IconButton
} from '@mui/material';
import MuiGrid from '../../components/MuiGrid';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { supabase } from '../../services/supabase';
import { LiveGame, Player } from '../../types/models';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Constants
const TOTAL_SEATS = 10;

// Item type for drag and drop
const ItemTypes = {
  PLAYER: 'player',
};

// Drag item interface
interface DragItem {
  type: string;
  id: string;
  index: number;
}

interface PlayerItem {
  player: Player;
  index: number;
  movePlayer: (dragIndex: number, hoverIndex: number) => void;
  removePlayer: (playerId: string) => void;
  disabled: boolean;
  draggedPlayerId: string | null;
  setDraggedPlayerId: React.Dispatch<React.SetStateAction<string | null>>;
}

const PlayerCard: React.FC<PlayerItem> = ({ player, index, movePlayer, removePlayer, disabled, draggedPlayerId, setDraggedPlayerId }) => {
  const ref = useRef<HTMLDivElement>(null);
  
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.PLAYER,
    item: () => {
      // Set dragged player ID when drag starts
      setDraggedPlayerId(player.id);
      return { type: ItemTypes.PLAYER, id: player.id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => {
      // Reset on drag end
      setDraggedPlayerId(null);
    },
    options: {
      dropEffect: 'move'
    }
  });

  const [, drop] = useDrop({
    accept: ItemTypes.PLAYER,
    hover: (item: DragItem) => {
      if (item.index === index) {
        return;
      }
      movePlayer(item.index, index);
      item.index = index;
    },
  });

  drag(drop(ref));

  return (
    <Box
      ref={ref}
      sx={{
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: 1,
        mb: 1,
        opacity: isDragging ? 0.5 : 1,
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'rgba(0, 0, 0, 0.04)',
        },
        p: 1,
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
      <Avatar
        sx={{ mr: 1 }}
        src={player.profile_picture_url || undefined}
      >
        {!player.profile_picture_url && <PersonIcon />}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body1">{player.nickname}</Typography>
        <Typography variant="body2" color="text.secondary">Seat {index + 1}</Typography>
      </Box>
      <IconButton 
        size="small" 
        color="error"
        onClick={() => removePlayer(player.id)}
        disabled={disabled}
      >
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};

const EmptySeat: React.FC<{
  index: number;
  movePlayer: (dragIndex: number, hoverIndex: number) => void;
}> = ({ index, movePlayer }) => {
  const ref = useRef<HTMLDivElement>(null);
  
  const [, drop] = useDrop({
    accept: ItemTypes.PLAYER,
    hover: (item: DragItem) => {
      if (item.index === index) {
        return;
      }
      movePlayer(item.index, index);
      item.index = index;
    },
  });

  drop(ref);

  return (
    <Box
      ref={ref}
      sx={{
        border: '1px dashed rgba(0, 0, 0, 0.12)',
        borderRadius: 1,
        mb: 1,
        height: '56px',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Empty Seat {index + 1}
      </Typography>
    </Box>
  );
};

const LiveGameSetup: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState<LiveGame | null>(null);
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);
  const [savingSeating, setSavingSeating] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  useEffect(() => {
    if (gameId) {
      fetchGameData(gameId);
      fetchPlayers();
    }
  }, [gameId]);

  const fetchGameData = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('live_games')
        .select(`
          *,
          league:league_id (
            id,
            name
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setGameData(data as LiveGame);
      
      // If the game already has players assigned, navigate to the management page
      const { data: playersData, error: playersError } = await supabase
        .from('live_game_players')
        .select('*')
        .eq('live_game_id', id);
        
      if (playersError) throw playersError;
      
      if (playersData && playersData.length > 0) {
        navigate(`/admin/live-game-management/${id}`);
      }
    } catch (error: any) {
      console.error('Error fetching game data:', error.message);
      showSnackbar('Failed to load game data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .order('nickname');

      if (error) throw error;
      setAvailablePlayers(data || []);
    } catch (error: any) {
      console.error('Error fetching players:', error.message);
      showSnackbar('Failed to load players', 'error');
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const addPlayerToSeat = (player: Player) => {
    // Add to selected players if not already selected
    if (!selectedPlayers.some(p => p.id === player.id)) {
      // If we already have TOTAL_SEATS, display a message
      if (selectedPlayers.length >= TOTAL_SEATS) {
        showSnackbar('Please remove a player before adding a new one', 'error');
        return;
      }
      
      setSelectedPlayers([...selectedPlayers, player]);
      
      // Remove from available players
      setAvailablePlayers(availablePlayers.filter(p => p.id !== player.id));
    }
  };

  const removePlayerFromSeat = (playerId: string) => {
    // Find player in selected players
    const player = selectedPlayers.find(p => p.id === playerId);
    if (player) {
      // Add back to available players
      setAvailablePlayers([...availablePlayers, player]);
      
      // Remove from selected players
      setSelectedPlayers(selectedPlayers.filter(p => p.id !== playerId));
    }
  };

  // Track the player being dragged to prevent multiple swaps
  const [draggedPlayerId, setDraggedPlayerId] = useState<string | null>(null);

  const movePlayer = (dragIndex: number, hoverIndex: number) => {
    // Only allow moving within bounds
    if (dragIndex < 0 || hoverIndex < 0 || 
        dragIndex >= selectedPlayers.length || 
        hoverIndex >= TOTAL_SEATS) {
      return;
    }

    const draggedPlayer = selectedPlayers[dragIndex];
    
    // If we're already tracking a different player being dragged, ignore hover events
    if (draggedPlayerId !== null && draggedPlayerId !== draggedPlayer.id) {
      return;
    }
    
    // Start tracking this player as being dragged
    if (draggedPlayerId === null) {
      setDraggedPlayerId(draggedPlayer.id);
    }
    
    const newSelectedPlayers = [...selectedPlayers];
    
    // Remove the player from the original position
    newSelectedPlayers.splice(dragIndex, 1);
    
    // Insert at the new position
    // If we're moving to a position beyond current list length, we just add it at the end
    if (hoverIndex >= newSelectedPlayers.length) {
      newSelectedPlayers.push(draggedPlayer);
    } else {
      newSelectedPlayers.splice(hoverIndex, 0, draggedPlayer);
    }
    
    setSelectedPlayers(newSelectedPlayers);
  };

  const shuffleSeats = () => {
    if (selectedPlayers.length !== TOTAL_SEATS) {
      showSnackbar(`You need exactly ${TOTAL_SEATS} players to shuffle seats`, 'error');
      return;
    }

    // Create a shuffled copy of players
    const shuffledPlayers = [...selectedPlayers].sort(() => 0.5 - Math.random());
    setSelectedPlayers(shuffledPlayers);
  };

  const handleStartGame = async () => {
    // Check if all seats are filled
    if (selectedPlayers.length !== TOTAL_SEATS) {
      showSnackbar(`You need exactly ${TOTAL_SEATS} players to start the game`, 'error');
      return;
    }

    setSavingSeating(true);
    try {
      // First, update the game status to ACTIVE
      const { error: gameUpdateError } = await supabase
        .from('live_games')
        .update({ status: 'ACTIVE' })
        .eq('id', gameId as string);

      if (gameUpdateError) throw gameUpdateError;

      // Prepare player assignments for the live game
      const playerAssignments = selectedPlayers.map((player, index) => ({
        live_game_id: gameId as string,
        player_id: player.id,
        seat_number: index + 1, // Seat numbers start from 1 in the database
        is_killed: false,
        role: 'CITIZEN' // Set all players to citizen by default
      }));

      // Insert player assignments
      const { error: playersError } = await supabase
        .from('live_game_players')
        .insert(playerAssignments);

      if (playersError) throw playersError;

      showSnackbar('Game started successfully!', 'success');
      
      // Navigate to the live game management page
      setTimeout(() => {
        navigate(`/admin/live-game-management/${gameId}`);
      }, 1500);
    } catch (error: any) {
      console.error('Error starting game:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    } finally {
      setSavingSeating(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Filter players based on search term
  const filteredAvailablePlayers = availablePlayers.filter(player => 
    player.nickname.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total number of selected players
  const totalSelected = selectedPlayers.length;
  const isFullySelected = totalSelected === TOTAL_SEATS;
  
  // Generate empty seat placeholders to reach TOTAL_SEATS
  const emptySeatsCount = Math.max(0, TOTAL_SEATS - totalSelected);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!gameData) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" color="error">
          Game not found or you do not have access to this game.
        </Typography>
        <Button 
          variant="contained" 
          sx={{ mt: 2 }}
          onClick={() => navigate('/admin')}
        >
          Return to Admin Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Live Game Setup
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {`${gameData.league?.name} - Session ${gameData.session_num}, Game ${gameData.number_in_session}`}
          </Typography>
          <Typography variant="body1">
            {`Judge: ${gameData.judge?.nickname}`}
          </Typography>
        </Box>

        <MuiGrid container spacing={3}>
          {/* Player Selection */}
          <MuiGrid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Available Players
              </Typography>
              
              <TextField
                fullWidth
                label="Search Players"
                variant="outlined"
                value={searchTerm}
                onChange={handleSearch}
                margin="normal"
                size="small"
              />
              
              <Box sx={{ mt: 2, maxHeight: '500px', overflow: 'auto' }}>
                <List>
                  {filteredAvailablePlayers.map((player) => (
                    <ListItem
                      key={player.id}
                      onClick={() => addPlayerToSeat(player)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: 'background.paper',
                        '&:hover': {
                          bgcolor: 'rgba(25, 118, 210, 0.08)'
                        },
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        borderRadius: 1,
                        mb: 1,
                        opacity: 1
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar>
                          {player.profile_picture_url ? (
                            <img 
                              src={player.profile_picture_url} 
                              alt={player.nickname}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <PersonIcon />
                          )}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={player.nickname} secondary={player.full_name} />
                    </ListItem>
                  ))}

                  {filteredAvailablePlayers.length === 0 && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'No matching players found' : 'No more players available'}
                      </Typography>
                    </Box>
                  )}
                </List>
              </Box>
            </Paper>
          </MuiGrid>

          {/* Seat Assignment */}
          <MuiGrid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Selected Players ({totalSelected}/{TOTAL_SEATS})
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={shuffleSeats}
                  disabled={totalSelected !== TOTAL_SEATS}
                >
                  Shuffle Seats
                </Button>
              </Box>
              
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {totalSelected < TOTAL_SEATS 
                  ? 'Click to add more players from the left panel' 
                  : 'Drag to reorder players between seats'}
              </Typography>
              
              <Box sx={{ mt: 2, maxHeight: '500px', overflow: 'auto' }}>
                {/* Render all selected players */}
                {selectedPlayers.map((player, index) => (
                  <PlayerCard 
                    key={player.id}
                    player={player}
                    index={index}
                    movePlayer={movePlayer}
                    removePlayer={removePlayerFromSeat}
                    disabled={false} // Allow removing players even when list is full
                    draggedPlayerId={draggedPlayerId}
                    setDraggedPlayerId={setDraggedPlayerId}
                  />
                ))}
                
                {/* Then add empty seats to reach 10 total */}
                {Array.from({ length: emptySeatsCount }).map((_, index) => (
                  <EmptySeat 
                    key={`empty-${index}`} 
                    index={totalSelected + index}
                    movePlayer={movePlayer}
                  />
                ))}
              </Box>
            </Paper>
          </MuiGrid>
        </MuiGrid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleStartGame}
            disabled={totalSelected !== TOTAL_SEATS || savingSeating}
            sx={{ minWidth: '200px' }}
          >
            {savingSeating ? <CircularProgress size={24} /> : 'Start Game'}
          </Button>
        </Box>

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={5000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </DndProvider>
  );
};

export default LiveGameSetup;